import { ExternalLink, PackageCheck, Pencil, Truck, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Order, OrderFulfillment } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { toast } from '@nucleus-commerce/ui/components/sonner';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useCancelFulfillment, useUpdateFulfillment } from '../hooks/use-order-mutations';
import { workflowOwnsOrder } from '../schemas/order-rules';

import { EditFulfillmentDialog } from './edit-fulfillment-dialog';

const STATUS_STYLES: Record<OrderFulfillment['status'], string> = {
  pending: 'bg-warning/20 text-warning-foreground border-transparent dark:bg-warning/25',
  shipped: 'bg-primary/10 text-foreground border-transparent',
  delivered: 'bg-success/15 text-success border-transparent dark:bg-success/20',
  cancelled: 'bg-muted text-muted-foreground border-transparent',
};

export interface OrderFulfillmentsCardProps {
  storeId: string;
  order: Order;
  canWrite: boolean;
}

/** Every parcel of the order with its tracking, an edit for the tracking and its next step. */
export function OrderFulfillmentsCard({ storeId, order, canWrite }: OrderFulfillmentsCardProps) {
  const { t } = useTranslation(['orders', 'common']);
  const format = useFormat();
  const message = useErrorMessage();
  const update = useUpdateFulfillment(storeId);
  const cancel = useCancelFulfillment(storeId);
  const [editing, setEditing] = useState<number | null>(null);
  const [cancelling, setCancelling] = useState<OrderFulfillment | null>(null);
  const titles = new Map(order.lines.map((line) => [line.id, line.title]));

  const step = (fulfillment: OrderFulfillment, status: 'shipped' | 'delivered') =>
    update.mutate(
      { id: order.id, fulfillmentId: fulfillment.id, body: { status } },
      { onError: (error) => toast.error(message(error)) },
    );
  const edited = editing === null ? undefined : order.fulfillments[editing];

  return (
    <Card className="gap-3">
      <CardHeader>
        <CardTitle>{t('fulfillments.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {order.fulfillments.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {workflowOwnsOrder(order) ? t('fulfillments.workflowOwns') : t('fulfillments.empty')}
          </p>
        ) : (
          <ul className="flex flex-col gap-4" aria-label={t('fulfillments.title')}>
            {order.fulfillments.map((fulfillment, index) => {
              const busy =
                (update.isPending && update.variables.fulfillmentId === fulfillment.id) ||
                (cancel.isPending && cancel.variables.fulfillmentId === fulfillment.id);
              const name = t('fulfillments.name', { index: index + 1 });
              return (
                <li key={fulfillment.id} className="flex flex-col gap-2 text-sm" aria-label={name}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{name}</span>
                      <Badge variant="outline" className={cn(STATUS_STYLES[fulfillment.status])}>
                        {t(`fulfillments.status.${fulfillment.status}`)}
                      </Badge>
                    </div>
                    <span
                      className="text-muted-foreground text-xs"
                      title={format.utc(fulfillment.createdAt)}
                    >
                      {fulfillment.deliveredAt
                        ? t('fulfillments.deliveredAt', {
                            when: format.relative(fulfillment.deliveredAt),
                          })
                        : fulfillment.shippedAt
                          ? t('fulfillments.shippedAt', {
                              when: format.relative(fulfillment.shippedAt),
                            })
                          : t('fulfillments.preparedAt', {
                              when: format.relative(fulfillment.createdAt),
                            })}
                    </span>
                  </div>
                  <ul className="text-muted-foreground flex flex-col gap-0.5">
                    {fulfillment.lines.map((line) => (
                      <li key={line.orderLineId}>
                        {line.quantity} ×{' '}
                        {titles.get(line.orderLineId) ?? t('fulfillments.unknownLine')}
                      </li>
                    ))}
                  </ul>
                  {fulfillment.trackingNumber || fulfillment.carrier ? (
                    <p className="flex flex-wrap items-center gap-1">
                      <Truck className="size-4" aria-hidden />
                      <span>
                        {[fulfillment.carrier, fulfillment.trackingNumber]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                      {fulfillment.trackingUrl ? (
                        <a
                          href={fulfillment.trackingUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 underline"
                        >
                          {t('fulfillments.track')}
                          <ExternalLink className="size-3" aria-hidden />
                        </a>
                      ) : null}
                    </p>
                  ) : (
                    <p className="text-muted-foreground text-xs">{t('fulfillments.noTracking')}</p>
                  )}
                  {fulfillment.note ? (
                    <p className="text-muted-foreground text-xs whitespace-pre-line">
                      {fulfillment.note}
                    </p>
                  ) : null}
                  {canWrite && fulfillment.status !== 'cancelled' ? (
                    <div className="flex flex-wrap gap-2">
                      {fulfillment.status !== 'delivered' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={() =>
                            step(
                              fulfillment,
                              fulfillment.status === 'pending' ? 'shipped' : 'delivered',
                            )
                          }
                        >
                          {fulfillment.status === 'pending' ? (
                            <Truck aria-hidden />
                          ) : (
                            <PackageCheck aria-hidden />
                          )}
                          {fulfillment.status === 'pending'
                            ? t('fulfillments.markShipped')
                            : t('fulfillments.markDelivered')}
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        aria-label={t('fulfillments.edit.action', { name })}
                        onClick={() => setEditing(index)}
                      >
                        <Pencil aria-hidden />
                        {t('fulfillments.edit.label')}
                      </Button>
                      {fulfillment.status === 'pending' ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busy}
                          aria-label={t('fulfillments.cancel.action', { name })}
                          onClick={() => setCancelling(fulfillment)}
                        >
                          <XCircle aria-hidden />
                          {t('fulfillments.cancel.label')}
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
      <AlertDialog
        open={cancelling !== null}
        onOpenChange={(open) => {
          if (!open) setCancelling(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('fulfillments.cancel.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('fulfillments.cancel.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.back')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const target = cancelling;
                setCancelling(null);
                if (target) {
                  cancel.mutate(
                    { id: order.id, fulfillmentId: target.id },
                    { onError: (error) => toast.error(message(error)) },
                  );
                }
              }}
            >
              {t('fulfillments.cancel.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {edited && editing !== null ? (
        <EditFulfillmentDialog
          storeId={storeId}
          order={order}
          fulfillment={edited}
          index={editing}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
        />
      ) : null}
    </Card>
  );
}
